import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Eye, 
  Target, DollarSign
} from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const Analytics = ({ isDarkMode = true }) => {
  const [timeRange, setTimeRange] = useState('30d');

  // Mock data
  const performanceData = [
    { month: 'Jan', requests: 45, completed: 38, avgTime: 3.2 },
    { month: 'Feb', requests: 52, completed: 45, avgTime: 2.8 },
    { month: 'Mar', requests: 48, completed: 42, avgTime: 3.1 },
    { month: 'Apr', requests: 61, completed: 55, avgTime: 2.9 },
    { month: 'May', requests: 58, completed: 52, avgTime: 2.7 },
    { month: 'Jun', requests: 65, completed: 58, avgTime: 2.5 }
  ];

  const categoryData = [
    { category: 'Ad Campaign', count: 25, percentage: 35 },
    { category: 'Social Media', count: 20, percentage: 28 },
    { category: 'Print Design', count: 12, percentage: 17 },
    { category: 'Digital Assets', count: 8, percentage: 11 },
    { category: 'Brand Identity', count: 5, percentage: 7 },
    { category: 'Other', count: 2, percentage: 2 }
  ];

  const keyMetrics = [
    { title: 'Total Revenue', value: 'SAR 820K', change: '12.5%', trend: 'up', icon: DollarSign, color: 'from-green-500 to-emerald-500' },
    { title: 'Total Spend', value: 'SAR 320K', change: '-2.3%', trend: 'down', icon: Target, color: 'from-red-500 to-rose-500' },
    { title: 'ROI', value: '2.6x', change: '8.7%', trend: 'up', icon: TrendingUp, color: 'from-purple-500 to-pink-500' },
    { title: 'Impressions', value: '2.2M', change: '15.2%', trend: 'up', icon: Eye, color: 'from-blue-500 to-indigo-500' }
  ];

  const campaignROI = [
    { name: 'Campaign A', roi: 15.2 },
    { name: 'Campaign B', roi: 12.8 },
    { name: 'Campaign C', roi: 18.5 }
  ];

  return (
    <div className="w-full h-full px-8 py-8 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0"
      >
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            Analytics Dashboard
          </h1>
          <p className="text-gray-300 mt-2 text-lg">
            Comprehensive insights into your creative performance and ROI
          </p>
        </div>
        <div className="flex space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
          >
            {/* <Download className="h-5 w-5" /> */}
            <span>Export</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Key Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {keyMetrics.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.1 }}
            className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-300">{metric.title}</p>
                <p className="text-2xl font-bold text-white mt-1">{metric.value}</p>
                <div className={`flex items-center mt-2 text-sm ${
                  metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {metric.trend === 'up' ? (
                    <TrendingUp className="h-4 w-4 mr-1" />
                  ) : (
                    <TrendingDown className="h-4 w-4 mr-1" />
                  )}
                  <span>{metric.change}</span>
                </div>
              </div>
              <div className={`p-3 rounded-xl bg-gradient-to-r ${metric.color}`}>
                <metric.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Performance Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10"
        >
          <h2 className="text-xl font-semibold text-white mb-6">Performance Trends</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={performanceData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)', 
                  borderRadius: '12px',
                  color: '#fff'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke="#8B5CF6" 
                strokeWidth={3}
                fillOpacity={1} 
                fill="url(#colorRevenue)" 
              />
              <Area 
                type="monotone" 
                dataKey="spend" 
                stroke="#EF4444" 
                strokeWidth={3}
                fillOpacity={1} 
                fill="url(#colorSpend)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Product Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10"
        >
          <h2 className="text-xl font-semibold text-white mb-6">Product Performance</h2>
          <div className="space-y-4">
            {categoryData.map((category, index) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-300"
              >
                {/* <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Award className="h-6 w-6 text-white" />
                </div> */}
                <div className="flex-1">
                  <p className="font-medium text-white">{category.category}</p>
                  <p className="text-sm text-gray-400">{category.count} projects</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-white">{category.percentage}%</p>
                  <p className="text-xs text-gray-400">of total projects</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Additional Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Campaign ROI Analysis</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {campaignROI.map((campaign, index) => (
            <motion.div
              key={campaign.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.1 }}
              className="text-center p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-300"
            >
              {/* <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Star className="h-8 w-8 text-white" />
              </div> */}
              <h3 className="font-semibold text-white mb-1">{campaign.name}</h3>
              <p className="text-2xl font-bold text-green-400 mb-1">{campaign.roi}%</p>
              <p className="text-sm text-gray-400">ROI</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Analytics; 